import axios from 'axios';
import type { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import { API_BASE_URL } from '../../constants/api';

interface Tokens {
  accessToken: string;
  refreshToken: string;
}

class HttpClient {
  private instance: AxiosInstance;
  private refreshPromise: Promise<Tokens> | null = null;
  private isRefreshing = false;

  constructor() {
    this.instance = axios.create({
      baseURL: API_BASE_URL,
      timeout: 10000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.setupInterceptors();
    this.startTokenRefreshTimer();
  }

  private redirectToLogin(reason: string) {
    console.error(`[HttpClient] 🔴 Redirecting to login. Reason: ${reason}`);
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('userData');
    window.location.href = '/login';
  }

  /**
   * Realiza el refresh del token de forma centralizada.
   * Si ya hay un refresh en curso, reutiliza la misma promise.
   * Reintenta una vez ante errores de red.
   */
  private async doRefresh(): Promise<Tokens> {
    if (this.refreshPromise) {
      console.log('[HttpClient] ⏳ Refresh already in progress, waiting...');
      return this.refreshPromise;
    }

    this.isRefreshing = true;

    this.refreshPromise = (async () => {
      const attemptRefresh = async (attempt: number): Promise<Tokens> => {
        try {
          const refreshToken = localStorage.getItem('refreshToken');
          if (!refreshToken) {
            throw new Error('No hay refresh token disponible');
          }

          console.log(`[HttpClient] 🔄 Attempting token refresh (attempt ${attempt})...`);

          const response = await axios.post(`${API_BASE_URL}/auth/refresh`, {
            refreshToken,
          });

          console.log('[HttpClient] ✅ Refresh response received:', response.status);

          // El backend envuelve respuestas en { data, statusCode, timestamp }
          const responseData = (response.data as any).data || response.data;
          const { accessToken, refreshToken: newRefreshToken } = responseData;

          if (!accessToken || !newRefreshToken) {
            console.error('[HttpClient] ❌ Invalid refresh response structure:', responseData);
            throw new Error('Respuesta de refresh inválida: faltan tokens');
          }

          console.log('[HttpClient] 💾 Storing new tokens');

          localStorage.setItem('accessToken', accessToken);
          localStorage.setItem('refreshToken', newRefreshToken);

          return { accessToken, refreshToken: newRefreshToken };
        } catch (error: any) {
          const status = error.response?.status;

          // Si es error de red (sin respuesta) y es el primer intento, reintentar
          if (!error.response && attempt === 1) {
            console.warn('[HttpClient] ⚠️ Network error on first attempt, retrying in 1s...');
            await new Promise((resolve) => setTimeout(resolve, 1000));
            return attemptRefresh(attempt + 1);
          }

          console.error('[HttpClient] ❌ Refresh failed:', {
            message: error.message,
            status,
            attempt,
          });

          // Solo redirigir al login si el error es de autenticación (401/403)
          if (status === 401 || status === 403) {
            this.redirectToLogin(`Refresh returned ${status}`);
          } else if (!error.response) {
            console.warn('[HttpClient] ⚠️ Network error during refresh. NOT redirecting to login.');
          } else {
            this.redirectToLogin(`Refresh returned ${status}: ${error.response?.data?.message || error.message}`);
          }

          throw error;
        }
      };

      try {
        return await attemptRefresh(1);
      } finally {
        this.isRefreshing = false;
        this.refreshPromise = null;
      }
    })();

    return this.refreshPromise;
  }

  private setupInterceptors() {
    // Request interceptor para agregar token de autenticación
    this.instance.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('accessToken');
        if (token && token !== 'undefined' && token !== 'null') {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error),
    );

    // Response interceptor para manejo de errores globales
    this.instance.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;
        const status = error.response?.status;
        const url = originalRequest?.url;

        // Solo manejar 401 o 403 en peticiones que no sean el refresh mismo
        if (
          (status === 401 || status === 403) &&
          !originalRequest._retry &&
          !url?.includes('/auth/refresh')
        ) {
          originalRequest._retry = true;
          console.log(`[HttpClient] 🔄 ${status} detected on ${url}, attempting token refresh...`);

          try {
            const tokens = await this.doRefresh();

            // Actualizar el header del request original
            originalRequest.headers.Authorization = `Bearer ${tokens.accessToken}`;

            // Reintentar la petición original
            return this.instance(originalRequest);
          } catch (refreshError) {
            // doRefresh ya manejó el error y redirigió si fue necesario
            return Promise.reject(refreshError);
          }
        }

        return Promise.reject(error);
      },
    );
  }

  async get<T = unknown>(
    url: string,
    config?: AxiosRequestConfig,
  ): Promise<AxiosResponse<T>> {
    return this.instance.get<T>(url, config);
  }

  async post<T = unknown>(
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig,
  ): Promise<AxiosResponse<T>> {
    return this.instance.post<T>(url, data, config);
  }

  async put<T = unknown>(
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig,
  ): Promise<AxiosResponse<T>> {
    return this.instance.put<T>(url, data, config);
  }

  async patch<T = unknown>(
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig,
  ): Promise<AxiosResponse<T>> {
    return this.instance.patch<T>(url, data, config);
  }

  async delete<T = unknown>(
    url: string,
    config?: AxiosRequestConfig,
  ): Promise<AxiosResponse<T>> {
    return this.instance.delete<T>(url, config);
  }

  private startTokenRefreshTimer() {
    // Renovar token cada 13 minutos (el access token dura 15 min)
    const REFRESH_INTERVAL = 13 * 60 * 1000;

    setInterval(async () => {
      const accessToken = localStorage.getItem('accessToken');
      if (accessToken && accessToken !== 'undefined' && accessToken !== 'null' && !this.isRefreshing) {
        try {
          await this.doRefresh();
        } catch {
          // doRefresh ya maneja el error
        }
      }
    }, REFRESH_INTERVAL);
  }
}

export const httpClient = new HttpClient();
